import React, { useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  type RouteObject,
} from 'react-router-dom';
import { useAppState } from './hooks/use-app-state';
import { useAuth } from './hooks/use-auth';
import { AppShell } from './components/layout/AppShell';
import { ErrorBoundary } from './components/error-boundary';
import { SkeletonStyles } from './components/ui/skeleton';
import { AuthPage } from './pages/auth/AuthPage';
import { AuthCallbackPage } from './pages/auth/AuthCallbackPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { ChatPage } from './pages/chat/ChatPage';
import { ResultsPage } from './pages/results/ResultsPage';
import { FavoritesPage } from './pages/favorites/FavoritesPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { OptinPage } from './pages/optin/OptinPage';

/** Apply theme to <html data-theme="..."> */
function useThemeEffect(theme: 'dark' | 'light') {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
}

const centeredScreen: React.CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--bg-base)',
  fontFamily: 'var(--font-mono)',
  fontSize: 14,
};

function LoadingScreen() {
  return <div style={{ ...centeredScreen, color: 'var(--text-muted)' }}>…</div>;
}

function AuthErrorScreen({ message }: { message: string }) {
  return (
    <div style={{ ...centeredScreen, flexDirection: 'column', gap: 12 }}>
      <span style={{ color: 'var(--danger, #e53e3e)', fontSize: 15, fontWeight: 600 }}>
        Authentication error
      </span>
      <span style={{ color: 'var(--text-muted)', fontSize: 12, maxWidth: 320, textAlign: 'center' }}>
        {message}
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          marginTop: 8,
          padding: '7px 16px',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: 'var(--bg-elevated)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: 13,
        }}
      >
        Retry
      </button>
    </div>
  );
}

export default function App() {
  const state = useAppState();
  const auth = useAuth();
  useThemeEffect(state.theme);

  // useMemo MUST be called before any conditional returns to satisfy Rules of Hooks.
  // All values used inside the factory are in the dependency array so stale closures
  // cannot form. state.toggleTheme/toggleLang/setLang and auth.signOut are stable
  // references (useCallback), so this only rebuilds when session, lang, or theme changes.
  const router = useMemo(() => {
    const userName =
      (auth.session?.user.user_metadata['full_name'] as string | undefined) ??
      auth.session?.user.email ??
      '';

    const routes: RouteObject[] = auth.session !== null
      ? [
          {
            path: '/',
            element: (
              <AppShell
                lang={state.lang}
                theme={state.theme}
                userName={userName}
                onLogout={auth.signOut}
                onToggleTheme={state.toggleTheme}
                onToggleLang={state.toggleLang}
              />
            ),
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              {
                path: 'dashboard',
                element: (
                  <ErrorBoundary>
                    <DashboardPage lang={state.lang} />
                  </ErrorBoundary>
                ),
              },
              {
                path: 'chat',
                element: (
                  <ErrorBoundary>
                    <ChatPage lang={state.lang} />
                  </ErrorBoundary>
                ),
              },
              {
                path: 'chat/:searchId',
                element: (
                  <ErrorBoundary>
                    <ChatPage lang={state.lang} />
                  </ErrorBoundary>
                ),
              },
              {
                path: 'results',
                element: (
                  <ErrorBoundary>
                    <ResultsPage lang={state.lang} />
                  </ErrorBoundary>
                ),
              },
              {
                path: 'results/:searchId',
                element: (
                  <ErrorBoundary>
                    <ResultsPage lang={state.lang} />
                  </ErrorBoundary>
                ),
              },
              {
                path: 'favorites',
                element: (
                  <ErrorBoundary>
                    <FavoritesPage lang={state.lang} />
                  </ErrorBoundary>
                ),
              },
              {
                path: 'settings',
                element: (
                  <ErrorBoundary>
                    <SettingsPage
                      lang={state.lang}
                      theme={state.theme}
                      onSetLang={state.setLang}
                      onToggleTheme={state.toggleTheme}
                    />
                  </ErrorBoundary>
                ),
              },
              { path: 'auth/reset-password', element: <ResetPasswordPage /> },
              { path: '*', element: <Navigate to="/dashboard" replace /> },
            ],
          },
          // Public standalone page (no AppShell) even when a recruiter is signed in.
          { path: '/optin', element: <OptinPage /> },
        ]
      : [
          {
            path: '/auth',
            element: (
              <AuthPage
                lang={state.lang}
                onToggleLang={state.toggleLang}
              />
            ),
          },
          { path: '/auth/callback', element: <AuthCallbackPage /> },
          { path: '/auth/reset-password', element: <ResetPasswordPage /> },
          { path: '/optin', element: <OptinPage /> },
          { path: '*', element: <Navigate to="/auth" replace /> },
        ];

    return createBrowserRouter(routes);
  }, [
    auth.session,
    auth.signOut,
    state.lang,
    state.theme,
    state.setLang,
    state.toggleTheme,
    state.toggleLang,
  ]);

  if (auth.loading) return <LoadingScreen />;
  if (auth.error !== null) return <AuthErrorScreen message={auth.error} />;

  return (
    <>
      <SkeletonStyles />
      <RouterProvider router={router} />
    </>
  );
}
